import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { dateUtils, type appLocale } from 'shared';

import {
  recentSearchesLib,
  type RecentSearch,
} from '@/entities/recent-searches';

import { getLocalizedMappedLocationLabel } from '@/shared/lib';

type Locale = appLocale.config.Locale;

const SEP = ' – ';

export function useRecentSearchCard(getSearch: () => RecentSearch) {
  const { locale } = useI18n();

  const isMultiTrip = computed(() => getSearch().routes.length > 1);

  const routeText = computed(() => {
    const uiLocale = locale.value as Locale;
    const search = getSearch();
    const { routes } = search;
    const [firstRoute] = routes;
    if (!firstRoute) {
      return '';
    }

    if (routes.length > 1) {
      const tripPattern =
        search.tripPattern ??
        recentSearchesLib.deriveRecentSearchTripPattern(routes);
      const dep = getLocalizedMappedLocationLabel(firstRoute.dep, uiLocale);
      const arr = getLocalizedMappedLocationLabel(
        tripPattern === 'roundTripMultiLeg'
          ? firstRoute.arr
          : routes[routes.length - 1]?.arr,
        uiLocale,
      );
      if (!dep || !arr) {
        return '';
      }
      return `${dep}${SEP}${arr}`;
    }

    const dep = getLocalizedMappedLocationLabel(firstRoute.dep, uiLocale);
    const arr = getLocalizedMappedLocationLabel(firstRoute.arr, uiLocale);
    if (!dep || !arr) {
      return '';
    }
    return `${dep}${SEP}${arr}`;
  });

  const routeParts = computed(() => {
    const [dep = routeText.value, arr = ''] = routeText.value.split(SEP, 2);
    return { dep, arr };
  });

  const datesText = computed(() => {
    const { routes } = getSearch();
    const [firstRoute] = routes;
    if (!firstRoute) {
      return '';
    }

    if (routes.length > 1) {
      const parts = routes
        .map(legRoute => legRoute.dates?.[0])
        .filter((d): d is string => Boolean(d))
        .map(d => dateUtils.getVerbalDate(d));
      if (!parts.length) {
        return '';
      }
      return parts.join(SEP);
    }

    const { dates } = firstRoute;
    if (!dates?.length) {
      return '';
    }

    const depDate = dateUtils.getVerbalDate(dates[0]);

    if (dates.length > 1 && dates[1]) {
      return `${depDate}${SEP}${dateUtils.getVerbalDate(dates[1])}`;
    }

    return depDate;
  });

  const passengersCount = computed(() => {
    const p = getSearch().passengers;
    return p.adt + p.chd + p.inf + p.yth;
  });

  return { isMultiTrip, routeText, routeParts, datesText, passengersCount };
}
